import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import ProductTariffFieldError from 'Components/ProductTariffFieldErrors'
import StyledLabel from 'Components/StyledLabel'
import {
  createSingleTariff,
  fetchAllTariffs,
  type Tariff,
  TariffStatus,
  updateSingleTariff
} from 'Services/ProductManagement/Features/Tariff/tariffSlice'
import { useGetVatDefinitionsQuery } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import { type LoadingStates, useAppDispatch } from 'Store'
import type { ProductTariffError, ProductTariffViolation } from 'Util/ApiClient'
import { All_TARIFFS_COUNT_PER_REQUEST, TARIFF_CODE_LENGTH } from 'Util/constants'
import { formatToUTCISO } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField
} from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import type React from 'react'
import { useEffect, useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

type FieldErrors = {
  [key: string]: string[]
}

const fieldErrorsDefaultState: FieldErrors = {
  tariffCode: [],
  tariffDescription: [],
  validFrom: [],
  validUntil: [],
  status: [],
  preOrderStartTimestamp: []
}

interface TariffFormProps {
  tariff?: Tariff
  successBaseUrl: string
  submitLoading: LoadingStates
}

const generateTariffValidityTitle = () => {
  return `tariff-validity-title`
}

export default function TariffForm({
  tariff,
  successBaseUrl,
  submitLoading
}: Readonly<TariffFormProps>) {
  const dispatch = useAppDispatch()

  const navigate = useNavigate()

  const isEdit = !!tariff

  const [tariffDetails, setTariffDetails] = useState<Partial<Tariff>>(isEdit ? tariff : {})

  const tariffValidity = tariffDetails?.active_period ?? null

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)

  const { data: vatDefinitions = [], isFetching: isVatDefinitionsFetching } =
    useGetVatDefinitionsQuery(undefined)

  useEffect(() => {
    if (isEdit && tariff) {
      setTariffDetails(tariff)
    }
  }, [tariff, isEdit])

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof Tariff
  ) => {
    if (value !== undefined) {
      setTariffDetails(prevState => ({ ...prevState, [field]: value }) as Tariff)
    }
  }

  const handleValidityDateTimePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = formatToUTCISO(value)
    }
    setTariffDetails(
      prevState =>
        ({
          ...prevState,
          active_period: {
            ...(prevState.active_period ?? null),
            [name]: formattedValue,
            title: generateTariffValidityTitle()
          }
        }) as Tariff
    )
  }

  const handlePreOrderStartTimestampChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = formatToUTCISO(value)
    }
    setTariffDetails(prevState => ({ ...prevState, [name]: formattedValue }) as Tariff)
  }

  const handleRadioButtonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target
    setTariffDetails(prevState => ({ ...prevState, [name]: value }))
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const tariffData = {
      ...tariffDetails,
      product_ids: tariffDetails.product_ids ?? []
    }

    if (tariff) {
      dispatch(updateSingleTariff({ ...tariffData, id: tariff.id }))
        .then(unwrapResult)
        .then(() => {
          SnackbarUtils.success(t`Tariff successfully updated.`)
          navigate(successBaseUrl)
        })
        .catch(({ violations }: ProductTariffError) => {
          if (violations && violations.length > 0) {
            setValidationErrors(violations)
          }
          SnackbarUtils.error(t`There was an error updating the tariff.`)
        })
      return
    }
    dispatch(createSingleTariff(tariffData))
      .then(unwrapResult)
      .then(() => {
        SnackbarUtils.success(t`Tariff successfully created.`)
        // Fetch all tariffs to update the sidebar tariff dropdown options
        dispatch(
          fetchAllTariffs({
            bypass_pagination: true,
            count: All_TARIFFS_COUNT_PER_REQUEST
          })
        )
        navigate(successBaseUrl)
      })
      .catch(({ violations }: ProductTariffError) => {
        if (violations && violations.length > 0) {
          setValidationErrors(violations)
        }
        SnackbarUtils.error(t`There was an error creating the tariff.`)
      })
  }

  function setValidationErrors(violations: ProductTariffViolation[]) {
    setFieldErrors(() => {
      // Reset the field errors to default state, so that the old errors won't be shown
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[violation.field] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  const tariffStatusOptions = [
    {
      label: t`Active`,
      value: TariffStatus.ACTIVE
    },
    {
      label: t`Passive`,
      value: TariffStatus.PASSIVE
    },
    {
      label: t`Draft`,
      value: TariffStatus.DRAFT
    },
    {
      label: t`Production ready`,
      value: TariffStatus.STAGED
    },
    {
      label: t`Pre-sale`,
      value: TariffStatus.ACTIVE_PRE_ORDER
    }
  ]

  const tariffStatusLabelId = useId()
  const isLoading = submitLoading === 'pending' || isVatDefinitionsFetching

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex' }}>
            <FormControl sx={{ minWidth: 240, mr: 3 }} size="small" required>
              <TextField
                name="tariff_code"
                required
                label={t`Tariff code`}
                size="small"
                value={tariffDetails?.tariff_code}
                slotProps={{
                  htmlInput: {
                    minLength: TARIFF_CODE_LENGTH,
                    maxLength: TARIFF_CODE_LENGTH
                  }
                }}
                error={fieldErrors.tariffCode.length > 0}
                onChange={e => handleChange(e.target.value, 'tariff_code')}
              />
              <ProductTariffFieldError error={fieldErrors.tariffCode} identifier="tariff_code" />
            </FormControl>

            <FormControl sx={{ minWidth: 240, mr: 3 }} size="small" required>
              <TextField
                name="description"
                required
                label={t`Tariff description`}
                size="small"
                value={tariffDetails?.description}
                error={fieldErrors.tariffDescription.length > 0}
                onChange={e => handleChange(e.target.value, 'description')}
              />
              <ProductTariffFieldError
                error={fieldErrors.tariffDescription}
                identifier="description"
              />
            </FormControl>
          </Box>
          <Box>
            <StyledLabel>{t`Active`}</StyledLabel>
            <Box sx={{ display: 'flex' }}>
              <CustomDateTimePicker
                name="valid_from"
                value={tariffValidity ? new Date(tariffValidity.valid_from) : null}
                dateToCompare={tariffValidity ? new Date(tariffValidity.valid_until) : null}
                dateToCompareFieldName="valid_until"
                isStartDate={true}
                onChange={handleValidityDateTimePickerChange}
                label={t`Active from`}
                isError={fieldErrors.validFrom?.length > 0}
                isRequired={true}
              >
                <ProductTariffFieldError error={fieldErrors.validFrom} identifier="valid_from" />
              </CustomDateTimePicker>

              <Box sx={{ p: 2 }}>{` - `}</Box>
              <CustomDateTimePicker
                name="valid_until"
                value={tariffValidity ? new Date(tariffValidity.valid_until) : null}
                dateToCompare={tariffValidity ? new Date(tariffValidity.valid_from) : null}
                dateToCompareFieldName="valid_from"
                isStartDate={false}
                onChange={handleValidityDateTimePickerChange}
                label={t`Active to`}
                isError={fieldErrors.validUntil?.length > 0}
                isRequired={true}
              >
                <ProductTariffFieldError error={fieldErrors.validUntil} identifier="valid_until" />
              </CustomDateTimePicker>
            </Box>
            <Box>
              <StyledLabel>{t`Pre-order start time`}</StyledLabel>
              <FormControl error={fieldErrors.preOrderStartTimestamp.length > 0}>
                <CustomDateTimePicker
                  name="pre_order_start_timestamp"
                  value={
                    tariffDetails?.pre_order_start_timestamp
                      ? new Date(tariffDetails.pre_order_start_timestamp)
                      : null
                  }
                  onChange={handlePreOrderStartTimestampChange}
                  label={t`Pre-order start time`}
                  isError={fieldErrors.preOrderStartTimestamp.length > 0}
                >
                  <ProductTariffFieldError
                    error={fieldErrors.preOrderStartTimestamp}
                    identifier="pre_order_start_timestamp"
                  />
                </CustomDateTimePicker>
              </FormControl>
            </Box>
          </Box>
          <Box>
            <StyledLabel>{t`VAT definition`}</StyledLabel>
            <Box sx={{ display: 'flex' }}>
              <FormControl sx={{ minWidth: 240, mr: 3 }} size="small" required>
                <InputLabel size="small">{t`VAT definition`}</InputLabel>
                <Select
                  name="vat_definition_id"
                  size="small"
                  value={tariffDetails.vat_definition_id ?? ''}
                  label={t`VAT definition`}
                  onChange={e => handleChange(e.target.value, 'vat_definition_id')}
                >
                  {vatDefinitions.map(vatDefinition => (
                    <MenuItem key={vatDefinition.id} value={vatDefinition.id}>
                      {vatDefinition.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box>
            <FormControl required error={fieldErrors.status.length > 0}>
              <StyledLabel id={tariffStatusLabelId}>{t`Tariff status`}</StyledLabel>
              <RadioGroup
                value={tariffDetails.status}
                onChange={handleRadioButtonChange}
                name="status"
                aria-labelledby={tariffStatusLabelId}
              >
                {tariffStatusOptions.map(({ label, value }) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={<Radio size="small" required />}
                    label={label}
                  />
                ))}
              </RadioGroup>
              <ProductTariffFieldError error={fieldErrors.status} identifier="status" />
            </FormControl>
          </Box>
          <CustomLoadingButton isLoading={isLoading} type="submit" size="large" iconPosition="end">
            {t`Save tariff`}
          </CustomLoadingButton>
        </Box>
      </Grid>
    </Grid>
  )
}
